import { useQuery } from "@tanstack/react-query";
import { useAgents } from "../admin/agents/useAgents";
import { useProducts } from "../admin/products/useProducts";
import { useCustomers } from "../customers/useCustomers";
import type { SavedOrderWithNames } from "./orderTypes";
import { listOrders } from "./ordersApi";
import { getOrdersIndex } from "./ordersIndex";

export const useOrders = (params: { from: string; to?: string; showHistorical: boolean }) => {
  const { data, isLoading: areOrdersLoading } = useQuery({
    queryKey: ["orders", params],
    queryFn: ({ signal }) => listOrders(params, signal),
  });
  const { products, isLoading: areProductsLoading } = useProducts();
  const { customers, isLoading: areCustomersLoading } = useCustomers();
  const { agents, isLoading: areAgentsLoading } = useAgents();

  const orders: SavedOrderWithNames[] = data
    ? data.map((o) => {
        const customer = customers.find((c) => c.id === o.customerId);
        const accountManager = customer?.accountManager;
        const customerContactPerson = customer?.contacts?.find(
          (c) => c.email === o.customerContactPersonEmail || c.loginEmail === o.customerContactPersonEmail,
        );
        return {
          ...o,
          supervisorName: agents.find((a) => a.id === o.supervisorId)?.name,
          customerName: customer?.name ?? "",
          customerContactPersonName: customerContactPerson?.name,
          customerContactPersonPhone: customerContactPerson?.phone,
          customerAccountManagerName: accountManager?.name,
          customerAccountManagerId: accountManager?.id,
          products: o.products.map((p) => {
            const product = products.find((pr) => pr.id === p.id);
            return {
              ...p,
              name: product?.name ?? "",
              providerName: product?.provider?.name ?? "",
            };
          }),
        };
      })
    : [];

  const ordersIndex = getOrdersIndex(orders ?? []);

  return {
    orders,
    ordersIndex,
    isLoading: areOrdersLoading || areProductsLoading || areCustomersLoading || areAgentsLoading,
  };
};
